import { useEffect, useState } from "react"
import styled from "styled-components"
import { v4 as uuidv4 } from "uuid"

import { useLazyGetInventoryQuery } from "store/api/widgetsApi"
import { FeatureFlag, isFeatureEnabled } from "store/featureFlagsSlice"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { showNewOrderModal } from "store/modalSlise"

import WidgetsLayout from "layouts/WidgetsLayout"
import { BarChart } from "components/common/BarChart"
import { Tooltip } from "components/common/Tooltip"

import styles from "./index.module.scss"

const SufficientTooltip = styled(Tooltip)`
  div {
    max-width: 350px;
  }
`

const initialState = {
  sufficientDays: 0,
  inventoryDevicesV1: 0,
  inventoryDevicesV2: 0,
  orderedDevices: 0,
}

const WidgetContent = styled.div`
  position: relative;
  margin-top: 20px !important;
`

const MainValueWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 16px 0 10px;
`

const MainLabel = styled.span`
  font-size: 12px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
`

const ValueWrapper = styled.div`
  margin-top: 4px;
  font-size: 24px;
  font-weight: 500;
  line-height: 1.5;
  color: #0f172a;
`

const ValueNote = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #64748b;
`

const WidgetFooter = styled.footer`
  border-top: 1px solid #f1f5f9;
  padding-top: 16px;
  display: flex;
  justify-content: center;
`

const WidgetFooterItem = styled.div`
  text-align: center;
  border-right: 1px solid #f1f5f9;
  flex: 1;
  padding: 0 4px;

  &:last-child {
    border: none;
  }
`

const FooterNote = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 12px;
  color: #64748b;

  &::before {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    display: inline-flex;
    content: "";
    flex-shrink: 0;
    background-color: ${({ $color }) => $color};
  }
`

interface Props {
  updateIndex: number
  variant?: "default" | "enterprise"
}

export const WidgetInventory = ({ updateIndex, variant = "default" }: Props) => {
  const dispatch = useAppDispatch()
  const [triggerGetInventory, { isFetching, data }] = useLazyGetInventoryQuery()
  const [widgetData, setWidgetData] = useState(initialState)
  const isEnterprise = variant === "enterprise"
  const thermv2ordering = useAppSelector(isFeatureEnabled(FeatureFlag.ThermV2Ordering))

  useEffect(() => {
    const fetchData = async () => {
      const res = await triggerGetInventory()
      if (res?.data?.data) {
        const data = res.data.data
        setWidgetData({
          sufficientDays: data.sufficient_days ?? 0,
          inventoryDevicesV1: data.inventory_devices_v1 ?? 0,
          inventoryDevicesV2: data.inventory_devices_v2 ?? 0,
          orderedDevices: data.ordered_devices ?? 0,
        })
      }
    }

    fetchData()
  }, [updateIndex, triggerGetInventory])

  const onNewClick = () => {
    dispatch(showNewOrderModal(true))
  }

  const chartTotal =
    widgetData.inventoryDevicesV1 + widgetData.orderedDevices + (thermv2ordering ? widgetData.inventoryDevicesV2 : 0)
  const loaded = !isFetching && !!data?.data
  const hasNoData = loaded && chartTotal === 0

  return (
    <WidgetsLayout
      title="Inventory"
      buttonText={isEnterprise ? undefined : "New order"}
      className={isEnterprise ? styles.enterpriseWidget : undefined}
      onButtonClick={onNewClick}
      isFetching={isFetching}
    >
      <WidgetContent className={isEnterprise ? styles.enterpriseContent : undefined}>
        <BarChart
          showEmptyState={hasNoData}
          className={isEnterprise ? styles.enterpriseBar : undefined}
          items={
            loaded
              ? [
                  { value: widgetData.inventoryDevicesV1, color: "#3B82F6" },
                  ...(thermv2ordering ? [{ value: widgetData.inventoryDevicesV2, color: "#1E3A8A" }] : []),
                  { value: widgetData.orderedDevices, color: thermv2ordering ? "#DBEAFE" : "#D7E6FA" },
                ]
              : [{ value: 1, color: "#E2E8F0" }]
          }
        />
        {loaded && !hasNoData && (
          <>
            <MainValueWrapper className={isEnterprise ? styles.enterpriseMainValue : undefined}>
              <MainLabel>
                <SufficientTooltip id={uuidv4()} text="SUFFICIENT FOR">
                  This indicator gauges sufficiency by averaging Nuve thermostats daily installations over the past 14
                  days. If, for example, the average rate is 10 and there are 150 thermostats in stock, the widget shows
                  &apos;Sufficient for 15 days,&apos; indicating the estimated stock duration based on historical rates.
                </SufficientTooltip>
              </MainLabel>
              <ValueWrapper>
                {widgetData.sufficientDays}
                <ValueNote> Days</ValueNote>
              </ValueWrapper>
            </MainValueWrapper>
            <WidgetFooter className={isEnterprise ? styles.enterpriseFooter : undefined}>
              {thermv2ordering ? (
                <>
                  <WidgetFooterItem>
                    <FooterNote $color="#3B82F6">
                      <Tooltip id={uuidv4()} text="In Inventory V1">
                        Number of available Nuve V1 thermostats in storage
                      </Tooltip>
                    </FooterNote>
                    <ValueWrapper>{widgetData.inventoryDevicesV1}</ValueWrapper>
                  </WidgetFooterItem>
                  <WidgetFooterItem>
                    <FooterNote $color="#1E3A8A">
                      <Tooltip id={uuidv4()} text="In Inventory V2">
                        Number of available Nuve V2 thermostats in storage
                      </Tooltip>
                    </FooterNote>
                    <ValueWrapper>{widgetData.inventoryDevicesV2}</ValueWrapper>
                  </WidgetFooterItem>
                  <WidgetFooterItem>
                    <FooterNote $color="#DBEAFE">
                      <Tooltip id={uuidv4()} text="Ordered">
                        Number of thermostats fulfilled (delivered).
                      </Tooltip>
                    </FooterNote>
                    <ValueWrapper>{widgetData.orderedDevices}</ValueWrapper>
                  </WidgetFooterItem>
                </>
              ) : (
                <>
                  <WidgetFooterItem>
                    <FooterNote $color="#3B82F6">
                      <Tooltip id={uuidv4()} text="In Inventory">
                        Number of available thermostats in storage
                      </Tooltip>
                    </FooterNote>
                    <ValueWrapper>{widgetData.inventoryDevicesV1}</ValueWrapper>
                  </WidgetFooterItem>
                  <WidgetFooterItem>
                    <FooterNote $color="#D7E6FA">
                      <Tooltip id={uuidv4()} text="Ordered">
                        Number of thermostats fulfilled (delivered).
                      </Tooltip>
                    </FooterNote>
                    <ValueWrapper>{widgetData.orderedDevices}</ValueWrapper>
                  </WidgetFooterItem>
                </>
              )}
            </WidgetFooter>
          </>
        )}
      </WidgetContent>
    </WidgetsLayout>
  )
}
